import React from 'react';
import { type ReactNode, useContext, useRef } from 'react';
import { cn } from '../../utils/ui';
import { DropdownContext } from './DropdownContext';
import { useClickOutside } from '../../hooks/useClickOutside';

/**
 * DropdownMenuContent Props Interface.
 */
export interface DropdownMenuContentProps {
    children: ReactNode;
    className?: string;
}

/**
 * The content container for dropdown items.
 * It uses the useClickOutside hook to close when clicking away.
 */
export const DropdownMenuContent: FC<DropdownMenuContentProps> = props => {
    const { children, className } = props;

    const context = useContext(DropdownContext);
    if (!context) throw new Error('DropdownMenuContent must be used within a DropdownMenu component.');

    const { open, setOpen, triggerRef } = context;
    const contentRef = useRef<HTMLDivElement>(null);

    // Close dropdown if click is outside content and not on the trigger
    useClickOutside(contentRef, event => {
        if (triggerRef.current && triggerRef.current.contains(event.target as Node)) return;
        setOpen(false);
    });

    if (!open) return null;

    return (
        <div ref={contentRef} className={cn('absolute mt-2 bg-gray-50 dark:bg-gray-950 rounded-lg shadow z-10', className)}>
            {children}
        </div>
    );
};
